/**
 * Per `nfc-point-cards` — point-card claim page.
 *   /redeem?key=CODE   → scan/tap landing
 *   /redeem            → manual code entry (also reachable from 功能/錢包)
 *
 * Logged out → stash the code + bounce to login; SignInCallback resumes
 * here. Active card → claim → credit + land on the bound character.
 * Already redeemed by me → straight to the character. Used by someone
 * else / void → message.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getStoredFanIdToken } from '../auth/fan-auth';
import { peekPointCard, redeemPointCard, type PointCardPeek } from '../api/fan-api';
import { useDocumentTitle } from '../lib/useDocumentTitle';

function normalize(code: string): string {
  return code.replace(/[\s-]/g, '').toUpperCase();
}

/**
 * Extract a card code from a scanned QR value. Cards encode the full
 * `/redeem?key=CODE` URL, but tolerate a bare code too.
 */
function parseQrToCode(raw: string): string {
  try {
    const u = new URL(raw);
    const k = u.searchParams.get('key');
    if (k) return normalize(k);
  } catch { /* not a URL — fall through */ }
  return normalize(raw);
}

// Minimal type for the native BarcodeDetector (not in lib.dom on the
// TS version we target). Present in Chromium / Android WebView; absent
// on iOS Safari, where we fall back to the device's native camera app.
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<Array<{ rawValue: string }>>;
}
type BarcodeDetectorCtor = new (opts?: { formats?: string[] }) => BarcodeDetectorLike;

/**
 * Live camera QR scanner overlay. Uses the native BarcodeDetector when
 * available; otherwise shows a graceful hint (the printed QR is a real
 * /redeem URL, so the OS camera app opens it directly). Always stops the
 * camera stream on close/unmount.
 */
function QrScanModal({ onCode, onClose }: { onCode: (code: string) => void; onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const liveRef = useRef(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    liveRef.current = true;
    const Ctor = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
    if (!Ctor) {
      setErr('此瀏覽器不支援掃描。請用手機「相機」App 對準卡片 QR(會自動開啟領取頁),或手動輸入序號。');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setErr('無法存取相機,請手動輸入序號。');
      return;
    }
    const detector = new Ctor({ formats: ['qr_code'] });
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        streamRef.current = stream;
        const v = videoRef.current;
        if (!v) return;
        v.srcObject = stream;
        v.setAttribute('playsinline', 'true');
        await v.play();
        const tick = async (): Promise<void> => {
          if (!liveRef.current) return;
          try {
            const found = await detector.detect(v);
            if (found && found.length > 0) {
              const code = parseQrToCode(found[0]!.rawValue);
              if (code) { liveRef.current = false; onCode(code); return; }
            }
          } catch { /* transient decode error — keep scanning */ }
          rafRef.current = requestAnimationFrame(() => { void tick(); });
        };
        rafRef.current = requestAnimationFrame(() => { void tick(); });
      } catch {
        setErr('無法開啟相機,請允許相機權限,或改用手動輸入。');
      }
    })();
    return () => {
      liveRef.current = false;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md space-y-3">
        {!err ? (
          <>
            <div className="relative overflow-hidden rounded-2xl border border-violet-500/40 bg-black aspect-square">
              <video ref={videoRef} className="h-full w-full object-cover" muted playsInline />
              <div className="pointer-events-none absolute inset-8 rounded-xl border-2 border-violet-400/70" />
            </div>
            <p className="text-center text-sm text-slate-300">將卡片上的 QR 對準框內</p>
          </>
        ) : (
          <p className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-center text-sm text-slate-300">{err}</p>
        )}
        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-lg border border-slate-600 bg-slate-800 py-3 text-slate-200 transition hover:bg-slate-700"
        >關閉</button>
      </div>
    </div>
  );
}

export function Redeem() {
  useDocumentTitle('領取點數卡');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const urlKey = normalize(params.get('key') ?? '');

  const [manual, setManual] = useState('');
  const [peek, setPeek] = useState<PointCardPeek | null>(null);
  const [code, setCode] = useState(urlKey);
  const [status, setStatus] = useState<'idle' | 'loading' | 'claiming' | 'done' | 'error'>('idle');
  const [msg, setMsg] = useState('');
  const [result, setResult] = useState<{ credited?: number; newBalance?: number; characterId?: string; characterGranted?: boolean } | null>(null);
  const [scanning, setScanning] = useState(false);
  const ranKey = useRef(false);

  const loggedIn = !!getStoredFanIdToken();

  // Shared by manual submit + QR scan: stash + login when logged out,
  // else peek the card.
  function submitCode(c: string): void {
    const norm = normalize(c);
    if (!norm) return;
    if (!loggedIn) {
      try { sessionStorage.setItem('pendingRedeemCode', norm); } catch { /* ignore */ }
      navigate('/login');
      return;
    }
    void loadPeek(norm);
  }

  async function loadPeek(c: string): Promise<void> {
    setStatus('loading'); setMsg(''); setPeek(null); setCode(c);
    try {
      const p = await peekPointCard(c);
      setPeek(p);
      if (p.status === 'redeemed' && p.redeemedByMe && p.characterId) {
        // Second open by the same fan → straight to the character.
        navigate(`/c/${p.characterId}/chat`, { replace: true });
        return;
      }
      if (p.status === 'redeemed') { setStatus('error'); setMsg('此卡已被使用。'); return; }
      if (p.status === 'void') { setStatus('error'); setMsg('此卡已失效。'); return; }
      setStatus('idle');
    } catch (e) {
      setStatus('error'); setMsg((e as Error).message || '查無此序號');
    }
  }

  // Auto-flow when arriving with ?key=.
  useEffect(() => {
    if (ranKey.current || !urlKey) return;
    ranKey.current = true;
    if (!loggedIn) {
      try { sessionStorage.setItem('pendingRedeemCode', urlKey); } catch { /* ignore */ }
      navigate('/login', { replace: true });
      return;
    }
    void loadPeek(urlKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function claim(): Promise<void> {
    setStatus('claiming'); setMsg('');
    try {
      const r = await redeemPointCard(code);
      if (r.alreadyRedeemed && r.characterId) { navigate(`/c/${r.characterId}/chat`, { replace: true }); return; }
      setResult({ credited: r.credited, newBalance: r.newBalance, characterId: r.characterId, characterGranted: r.characterGranted });
      setStatus('done');
    } catch (e) {
      setStatus('error'); setMsg((e as Error).message || '領取失敗');
    }
  }

  const card = 'rounded-2xl border border-slate-800 bg-slate-900 p-5 sm:p-6 max-w-md w-full space-y-4';

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-50 px-3 py-6">
      <div className={card}>
        <div className="text-center">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-violet-400">NOXOR</div>
          <h1 className="text-xl font-semibold">領取點數卡</h1>
        </div>

        {/* Manual entry (no key, or peek failed/idle without a card) */}
        {!peek && status !== 'loading' && (
          <div className="space-y-3">
            <p className="text-sm text-slate-400 text-center">輸入卡片序號,或掃描卡片上的 QR / NFC。</p>
            <input
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              placeholder="XXXX-XXXX-XXXX-XXXX"
              className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-3 text-center font-mono tracking-widest text-slate-100 focus:border-violet-500 focus:outline-none"
            />
            <button
              type="button"
              disabled={!manual.trim()}
              onClick={() => submitCode(manual)}
              className="w-full rounded-lg bg-violet-500 hover:bg-violet-400 active:scale-[0.98] text-white font-medium py-3 transition disabled:opacity-50"
            >查詢序號</button>
            <button
              type="button"
              onClick={() => setScanning(true)}
              className="w-full rounded-lg border border-violet-500/40 bg-violet-500/10 hover:bg-violet-500/20 text-violet-200 font-medium py-3 transition"
            >📷 掃描 QR</button>
            {status === 'error' && <p className="text-sm text-rose-300 text-center">{msg}</p>}
          </div>
        )}

        {scanning && (
          <QrScanModal
            onCode={(c) => { setScanning(false); submitCode(c); }}
            onClose={() => setScanning(false)}
          />
        )}

        {status === 'loading' && <p className="text-sm text-slate-400 text-center py-6">查詢中…</p>}

        {/* Active card → claim */}
        {peek && peek.status === 'active' && status !== 'done' && (
          <div className="space-y-4">
            <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4 text-center">
              <div className="text-3xl font-bold text-violet-200">{peek.faceValue} <span className="text-base font-normal text-slate-400">點</span></div>
              {peek.grantCharacter && peek.characterName && (
                <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                  🎁 內含角色「{peek.characterName}」
                  {peek.alreadyOwned && <div className="mt-0.5 text-[11px] text-slate-400">你已擁有這個角色,兌換後將獲得點數</div>}
                </div>
              )}
              {!peek.grantCharacter && peek.characterName && <div className="mt-1 text-sm text-slate-300">可用於與「{peek.characterName}」對話</div>}
              <div className="mt-1 text-[11px] text-slate-500">{peek.currency === 'operator' ? '經營者幣' : '平台幣（黑曜石）'}</div>
            </div>
            <button
              type="button"
              disabled={status === 'claiming'}
              onClick={() => void claim()}
              className="w-full rounded-lg bg-violet-500 hover:bg-violet-400 active:scale-[0.98] text-white font-medium py-3 transition disabled:opacity-50"
            >{status === 'claiming' ? '領取中…' : '確認領取'}</button>
            {status === 'error' && <p className="text-sm text-rose-300 text-center">{msg}</p>}
          </div>
        )}

        {/* Done */}
        {status === 'done' && result && (
          <div className="space-y-4 text-center">
            <div className="h-16 w-16 mx-auto rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center text-emerald-300 text-3xl">✓</div>
            <div className="text-lg font-semibold">已領取 {result.credited} 點!</div>
            {result.characterGranted && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">🎁 角色已加入你的收藏!</div>
            )}
            {typeof result.newBalance === 'number' && <div className="text-sm text-slate-400">目前餘額:{result.newBalance} 點</div>}
            <div className="flex flex-col gap-2">
              {result.characterId && (
                <button onClick={() => navigate(`/c/${result.characterId}/chat`)} className="rounded-lg bg-violet-500 hover:bg-violet-400 text-white font-medium py-3 transition">開始對話</button>
              )}
              <button onClick={() => navigate('/wallet')} className="rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 py-3 transition">查看錢包</button>
            </div>
          </div>
        )}

        {/* Terminal error (used/void) with a retry to manual */}
        {status === 'error' && peek && peek.status !== 'active' && (
          <button onClick={() => { setPeek(null); setStatus('idle'); setManual(''); }} className="w-full rounded-lg border border-slate-700 bg-slate-800 text-slate-200 py-3 transition">輸入其他序號</button>
        )}
      </div>
    </div>
  );
}
